import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import classes from "./HPOConstants";
import MonarchLink from "./MonarchLink";
import OmimLink from "./OmimLink";
import NCITLink from "./NCITLink";
import OrphanetLink from "./OrphanetLink";

function HPOItem(props) {
  const {
    checkBoxes,
    item,
    showMonarchLink,
    onCheckChanged,
    type,
    updateNumberOfSelections,
  } = props;
  const { checked } = item;
  const [isExpanded, setExpanded] = useState(Boolean(item.expanded));
  const [isSelected, setSelected] = useState(Boolean(checked));
  const [children, setChildren] = useState(item.phenotypes || []);
  const name = item.name || item.disease_name || item.phenotype_name;
  const hasChildren = Boolean(children.length);

  // certain forms depent on the item -- so the reference to the hierarchy should remain the same.

  const handleChildChecked = useCallback((isChildSelected) => {
    if (isChildSelected) {
      item.checked = true;
      setSelected(item.checked);
    }
  }, []);

  const updateExpanded = (val) => {
    item.expanded = val;
    setExpanded(val);
  };

  const constructId = useCallback((x) => {
    const id =
      x.disease_id || x.phenotype_id || x.phenotype_name || x.disease_name;
    return `${id}-${x.checked}-${new Date().getTime()}`;
  });

  const handleCheck = () => {
    const newState = !isSelected;
    item.checked = newState;
    setSelected(newState);
    if (item.phenotypes && item.checked) {
      updateExpanded(true);
    }
    onCheckChanged(newState);
    if (!newState) {
      item.phenotypes = [...children].map((x) => ({ ...x, checked: false }));
      setChildren(item.phenotypes);
    }
    updateNumberOfSelections();
  };

  const containerClasses = [
    "tw-flex tw-px-1 tw-items-center",
    hasChildren ? "tw-cursor-pointer hover:tw-opacity-100" : "",
    classes[type],
  ];

  return (
    <div className={isExpanded && hasChildren ? "tw-mb-1" : ""}>
      <div className={containerClasses.join(" ")}>
        {checkBoxes ? (
          <div className="tw-inline-block">
            <label className="tw-flex tw-items-center tw-m-0 tw-cursor-pointer">
              <input
                type="checkbox"
                className="tw-cursor-pointer"
                checked={isSelected}
                onChange={handleCheck}
              />
              <span className="hover:tw-underline tw-pl-2">
                {name}
                {hasChildren ? (
                  <span
                    className="tw-pl-2 tw-opacity-60"
                    data-tooltip={`${children.length} Phenotype${
                      children.length === 1 ? "" : "s"
                    }`}
                    data-tooltip-delay={1000}
                  >
                    {hasChildren ? `(${children.length})` : ""}
                  </span>
                ) : null}
              </span>
            </label>
          </div>
        ) : (
          <div
            className="tw-pl-2 tw-py-1"
            onClick={() => updateExpanded(!isExpanded)}
          >
            {name}
            {hasChildren ? (
              <span
                className="tw-pl-2 tw-opacity-60"
                data-tooltip={`${children.length} Phenotype${
                  children.length === 1 ? "" : "s"
                }`}
                data-tooltip-delay={1000}
              >
                {hasChildren ? `(${children.length})` : ""}
              </span>
            ) : null}
          </div>
        )}

        <div
          onClick={() => updateExpanded(!isExpanded)}
          className="tw-flex-grow tw-py-3"
        />

        {showMonarchLink ? (
          <div className="tw-flex-shrink-0">
            <MonarchLink item={item} type={type} />
          </div>
        ) : null}
        {item.omim_id ? (
          <div className="tw-flex-shrink-0">
            <OmimLink id={item.omim_id} />
          </div>
        ) : null}
        {item.ncit_id ? (
          <div className="tw-flex-shrink-0">
            <NCITLink id={item.ncit_id} />
          </div>
        ) : null}
        {item.orphanet_id ? (
          <div className="tw-flex-shrink-0">
            <OrphanetLink id={item.orphanet_id} />
          </div>
        ) : null}
        {hasChildren ? (
          <div>
            <i
              onClick={() => updateExpanded(!isExpanded)}
              className={`varsome-icons varsome-right tw-ml-4 ${
                isExpanded ? "icon-chevron-up" : "icon-chevron-down"
              }`}
            />
          </div>
        ) : (
          <div className="tw-w-3" />
        )}
      </div>
      {isExpanded ? (
        <div className="tw-ml-4">
          {children.map((x) => (
            <HPOItem
              {...props}
              onCheckChanged={handleChildChecked}
              key={constructId(x)}
              item={x}
              type="phenotype"
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

HPOItem.propTypes = {
  checkBoxes: PropTypes.bool,
  showMonarchLink: PropTypes.bool,
  item: PropTypes.shape({
    phenotypes: PropTypes.arrayOf(PropTypes.shape({})),
    checked: PropTypes.bool,
    omim_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ncit_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orphanet_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disease_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    phenotype_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    disease_name: PropTypes.string,
    phenotype_name: PropTypes.string,
    name: PropTypes.string,
    expanded: PropTypes.bool,
  }).isRequired,
  updateNumberOfSelections: PropTypes.func,
  onCheckChanged: PropTypes.func,
  type: PropTypes.oneOf(["disease", "phenotype", "gene"]).isRequired,
};

HPOItem.defaultProps = {
  showMonarchLink: true,
  checkBoxes: false,
  onCheckChanged: noop,
  updateNumberOfSelections: noop,
};

export default HPOItem;
